/**
 * Turning stored bars into chart points, including the intervals that have no bar.
 *
 * Kept apart from `CandleChart.tsx` because this is the part with a decision in it and no
 * dependency on the charting library — so it can be tested without a DOM, and so the test
 * asserts the arithmetic rather than a canvas.
 *
 * ## Why the gaps have to be manufactured
 *
 * Lightweight Charts packs the points it is given side by side. Hand it a series missing six
 * hours and it draws two adjacent candles six hours apart, with nothing between them to say
 * so. That is `data_gaps`' whole reason for existing, reproduced in pixels: **finding F12 was
 * 536 minutes of missing bars that every reader saw as a complete range.**
 *
 * A *whitespace point* — a time with no prices — is the library's own way to say "this
 * interval exists and has no data". So the series it receives is the bars plus one whitespace
 * point per missing interval between them.
 *
 * Note what this does **not** do: it does not distinguish a hole the venue caused from one
 * this platform caused. `find_interior_gaps_with_source` can (finding F11 — below a
 * `rest-backfill` bar a one-interval hole is ordinary silence on a thin pair, below a
 * `ws-stream` bar it is data loss), but the bars endpoint does not carry `source`, and the
 * count of *recorded* gaps in the legend comes from `/data-health`, which applies that
 * judgement server-side. This function draws every hole; the legend says how many of them
 * anybody has decided are real.
 */

export interface BarLike {
  ts: string;
  open: string;
  high: string;
  low: string;
  close: string;
}

export interface CandlePoint {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface WhitespacePoint {
  time: number;
}

export type ChartPoint = CandlePoint | WhitespacePoint;

/**
 * Enough whitespace to make any real hole visible, far short of what would stall a tab.
 *
 * A four-day hole on a 1m series is 5,760 points; a year is half a million. Past the bound
 * the gap renders as one wide blank instead of many narrow ones, which reads identically and
 * costs nothing — whereas allocating half a million objects to draw empty space is how a
 * chart of a long-abandoned symbol hangs the browser.
 */
export const MAX_WHITESPACE = 5_000;

export function isWhitespace(point: ChartPoint): point is WhitespacePoint {
  return !('close' in point);
}

/** Seconds since the epoch for an ISO 8601 UTC timestamp. */
export function secondsOf(ts: string): number {
  return Math.floor(Date.parse(ts) / 1000);
}

/**
 * Bars as chart points, with a whitespace point for every interval that has none.
 *
 * `toNumber` is passed in rather than imported so this module stays free of the decimal
 * dependency and the caller has to name the lossy step (`money.toPlot`) at the call site.
 */
export function withGaps(
  bars: BarLike[],
  intervalSeconds: number,
  toNumber: (value: string) => number,
): ChartPoint[] {
  if (intervalSeconds <= 0) throw new Error(`interval must be positive, got ${intervalSeconds}`);

  const points: ChartPoint[] = [];
  let previous: number | null = null;

  for (const bar of bars) {
    const at = secondsOf(bar.ts);
    if (previous !== null) {
      const missing = Math.floor((at - previous) / intervalSeconds) - 1;
      const draw = Math.min(Math.max(missing, 0), MAX_WHITESPACE);
      for (let n = 1; n <= draw; n += 1) {
        points.push({ time: previous + n * intervalSeconds });
      }
    }
    points.push({
      time: at,
      open: toNumber(bar.open),
      high: toNumber(bar.high),
      low: toNumber(bar.low),
      close: toNumber(bar.close),
    });
    previous = at;
  }
  return points;
}
